import React, { useEffect } from 'react';
import { autoId, formFieldErrorPropsV2 } from '@workday/upc-common';
import '../../SettingsModalContent.scss';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import { ID_CANCEL_BUTTON, ID_PASSWORD, ID_SUBMIT_BUTTON } from '../../../../autoIds';
import { useVerifyPasswordMutation, VerifyPasswordOperation } from '../../../../slices/account-api.slice';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { FormField } from '@workday/canvas-kit-react';
import styles from '../../../../feature-auth/login/loginPasswordEntry/LoginPasswordEntry.module.scss';
import { PasswordTextInput } from '../../../../shared/password-input/password-input';
import { serverError } from '../../../../utils/server-error';
import { PasswordFormState } from '../../../../slices/types';
import { useErrorHandling } from '../../../../hooks/useErrorHandling';

interface VerifyPasswordEntryProps {
    onClose: () => void;
    onNext: () => void;
    operation: VerifyPasswordOperation;
}

export function VerifyPasswordEntry({ onClose, onNext, operation }: VerifyPasswordEntryProps) {
    const { t } = useTranslation('account');

    const { register, handleSubmit, setError, formState } = useForm<PasswordFormState>({
        defaultValues: {
            password: ''
        }
    });

    const [verifyPassword, verifyPasswordMutation] = useVerifyPasswordMutation();

    useEffect(() => {
        if (verifyPasswordMutation.isSuccess) {
            onNext();
        }
    }, [verifyPasswordMutation.isSuccess, onNext]);

    useErrorHandling(verifyPasswordMutation.error, {
        inlineErrors: ['UPC-1034', 'UPC-1054', 'UPC-1066'],
        onInlineError: error => setError('password', serverError(error))
    });

    function onSubmit({ password }) {
        verifyPassword({ password, operation });
    }

    return (
        <div className='settingsModalContent'>
            <form onSubmit={handleSubmit(onSubmit)}>
                <h1>{t('VerifyPasswordEntry.confirmPassword')}</h1>
                <h2>{t('VerifyPasswordEntry.currentPassword')}</h2>
                <br />
                <FormField
                    className={styles.formField}
                    grow={true}
                    label={t('VerifyPasswordEntry.passwordFieldLabel')}
                    id='password'
                    {...formFieldErrorPropsV2(
                        formState.errors.password,
                        undefined,
                        t('Required.fieldErrorLabel', { ns: 'shared' })
                    )}>
                    <PasswordTextInput
                        {...autoId(ID_PASSWORD)}
                        autoFocus={true}
                        autoComplete='current-password'
                        disabled={verifyPasswordMutation.isLoading}
                        placeholder={t('VerifyPasswordEntry.passwordInputPlaceholder') || undefined}
                        {...register('password', {
                            required: t('Required.fieldGeneric', { ns: 'shared' })
                        })}
                    />
                </FormField>
                <div className='settingsModalContent__footer'>
                    <PrimaryButton
                        {...autoId(ID_SUBMIT_BUTTON)}
                        type='submit'
                        size='large'
                        disabled={verifyPasswordMutation.isLoading}
                        grow={true}>
                        {t('VerifyPasswordEntry.submitButton')}
                    </PrimaryButton>
                    <br />
                    <TertiaryButton
                        className='settingsModalContent__cancel'
                        {...autoId(ID_CANCEL_BUTTON)}
                        type='button'
                        size='large'
                        onClick={onClose}>
                        {t('Common.cancel', { ns: 'shared' })}
                    </TertiaryButton>
                </div>
            </form>
        </div>
    );
}
